import { Button } from '@heroui/react';
import { ColumnDef } from '@tanstack/react-table';
import { Trash2 } from 'lucide-react';
import React, { FC, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ClientRequestFirestoreResponse } from 'src/types';

import {
  ContentLoadError,
  DataTable,
  ModalConfirmation,
  PageTitle,
  PageWrapper,
} from '../../../../components';
import { useHeroToast } from '../../../../hooks';
import {
  useDeleteClientRequestFromFirestore,
  useGetClientRequestsFromFirestore,
} from '../../../../queries';
import { ClientRequestModal } from '../../components';
import { ClientRequestsColumns } from './columns';

const PAGE_SIZE = 20;

export const ClientRequests: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Number(searchParams.get('page') ?? '1');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { showSuccess, showError } = useHeroToast();
  const { mutate: deleteRequest, isPending: isDeletePending } = useDeleteClientRequestFromFirestore();

  const { data: clientRequests, error } = useGetClientRequestsFromFirestore(PAGE_SIZE, currentPage);

  const handleDeleteConfirm = () => {
    if (!deleteId) return;
    deleteRequest(
      { id: deleteId },
      {
        onSuccess: () => {
          showSuccess('Zgłoszenie zostało usunięte');
          setDeleteId(null);
        },
        onError: () => showError('Nie udało się usunąć zgłoszenia'),
      },
    );
  };

  const handlePageChange = (page: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', String(page));
      return next;
    });
  };

  const selectedRequest = useMemo(
    () => clientRequests?.data.find((item) => item.id === selectedId) ?? null,
    [clientRequests?.data, selectedId],
  );

  const columns: ColumnDef<ClientRequestFirestoreResponse>[] = useMemo(
    () => [
      ...ClientRequestsColumns,
      {
        header: '',
        id: 'actions',
        enableSorting: false,
        meta: { align: 'right' },
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-2">
            <Button
              size="sm"
              variant="flat"
              radius="sm"
              color="primary"
              onPress={() => setSelectedId(row.original.id)}
            >
              Otwórz
            </Button>
            <Button
              isIconOnly
              size="sm"
              variant="light"
              radius="sm"
              aria-label="Usuń zgłoszenie"
              onPress={() => setDeleteId(row.original.id)}
            >
              <Trash2 size={16} color="#ef4444" />
            </Button>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <PageWrapper>
      {error ? (
        <ContentLoadError />
      ) : (
        <>
          <PageTitle title="Zgłoszenia klientów" />
          <DataTable
            data={clientRequests?.data ?? []}
            columns={columns}
            isLoading={clientRequests?.isLoading}
            isError={!!error}
            pageSize={PAGE_SIZE}
            totalPages={clientRequests?.totalPages ?? 1}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            searchPlaceholder="Szukaj po temacie, e-mailu, hotelu..."
          />
          <ClientRequestModal
            request={selectedRequest}
            isOpen={!!selectedRequest}
            onClose={() => setSelectedId(null)}
          />
          <ModalConfirmation
            needConfirm={false}
            confirmValue=""
            isOpen={!!deleteId}
            onClose={() => setDeleteId(null)}
            onConfirm={handleDeleteConfirm}
            title="Usuń zgłoszenie"
            description="Czy na pewno chcesz usunąć to zgłoszenie? Tej operacji nie można cofnąć."
            isLoading={isDeletePending}
          />
        </>
      )}
    </PageWrapper>
  );
};
